import * as Api from '@ViewModels';
import { StyleSheet, css } from 'aphrodite';
import * as React from 'react';
import { useParams } from 'react-router';
import { COPY_MAGE_ASSISTANT_NAME, CopyMageAiChatLayout, CopyMageChatPrompt } from '.';
import { useErrorMessages, useFullscreenModal } from '../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../models/Logging';
import { useAIAssistantConversationStarterQuery } from '../../../queries/AI/useAIAssistantConversationStarterQuery';
import { useAIAssistantCreateConversationMutation } from '../../../queries/AI/useAIAssistantCreateConversationMutation';
import { invalidateAllAIConversationFilterQueries } from '../../../queries/AIConversation/useAIConversationFilterQuery';
import { AIAssistantThinkingImageUrl } from '../../../web/components/ai/AIContentGenerationWizard/models';
import { Avatar2 } from '../../../web/components/Avatar2';
import { SparklesIcon } from '../../../web/components/svgs/icons/SparklesIcon';
import { brandPrimary, charmGray, shuffleGray, titles, white } from '../../../web/styles/colors';

const s = StyleSheet.create({
	root: {
		display: 'flex',
		flexDirection: 'column',
		height: '100%',
	},
	starterRoot: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		padding: `32px 16px`,
		maxWidth: 608,
		width: '100%',
		margin: '0 auto',
		'@media (min-width: 768px)': {
			paddingTop: 48,
			paddingBottom: 48,
		},
	},
	sparklesIcon: {
		width: 20,
		height: 20,
		color: brandPrimary,
	},
	starterHeader: {
		display: 'flex',
		alignItems: 'center',
		gap: 8,
		color: brandPrimary,
		marginBottom: 16,
		fontSize: '0.875rem',
	},
	starterAskLev: {
		fontSize: '0.875rem',
		fontWeight: 'bold',
	},
	assistantThinkingAvatar: {
		width: 84,
		height: 84,
		marginBottom: 12,
	},
	helpMsg: {
		fontSize: '1.5rem',
		fontWeight: 'bold',
		textAlign: 'center',
		color: titles,
		marginBottom: 24,
	},
	starterGrid: {
		display: 'flex',
		gap: 16,
		justifyContent: 'center',
		maxWidth: 512,
		width: '100%',
		marginBottom: 56,
		flexWrap: 'wrap',
	},
	starterCard: {
		boxSizing: 'border-box',
		flexBasis: 248,
		padding: `16px 24px`,
		border: `1px solid ${shuffleGray}`,
		display: 'flex',
		flexDirection: 'column',
		gap: 8,
		borderRadius: 12,
		backgroundColor: white,
		':hover': {
			backgroundColor: '#18181b0d',
		},
	},
	starterCardHeader: {
		fontSize: '0.875rem',
		color: titles,
	},
	starterCardSubtext: {
		fontSize: '0.75rem',
		color: charmGray,
	},
	loadingContainer: {
		display: `flex`,
		justifyContent: 'center',
	},
	starterInput: {
		background: white,
		width: '100%',
	},
});

export function CopyMageConversationStarterPage() {
	const errorMessages = useErrorMessages();
	const fullscreenModal = useFullscreenModal();
	const logger = useEventLogging('CopyMageConversationStarter');
	const { accountId } = useParams<{ accountId: string }>();
	const impersonationContext: Api.IImpersonationContext = { account: { id: accountId } };
	const [prompt, setPrompt] = React.useState('');
	const aiAssistantConversationStarterQuery = useAIAssistantConversationStarterQuery({
		assistantName: COPY_MAGE_ASSISTANT_NAME,
	});
	const aiAssistantCreateConversationMutation = useAIAssistantCreateConversationMutation({
		onError: error => {
			errorMessages.pushApiError(error);
			logger.logApiError('aiAssistantCreateConversationMutation', error);
		},
		onSuccess: () => {
			invalidateAllAIConversationFilterQueries();
		},
	});
	const handleStartConversation = async (starterPrompt: string) => {
		if (!starterPrompt) {
			return;
		}
		setPrompt('');
		const conversation = await aiAssistantCreateConversationMutation.mutateAsync({
			assistantName: COPY_MAGE_ASSISTANT_NAME,
			impersonationContext,
		});
		fullscreenModal.history.push(`/copymage/${accountId}/conversation/${conversation.id}`, {
			starterMessages: [
				{
					role: Api.AIChatRole.User,
					text: starterPrompt,
					id: null,
				},
			],
		});
	};
	const handleStarterPicked = async (starter: Api.AIConversationStarter) => {
		const conversation = await aiAssistantCreateConversationMutation.mutateAsync({
			assistantName: COPY_MAGE_ASSISTANT_NAME,
			impersonationContext,
		});
		fullscreenModal.history.push(`/copymage/${accountId}/conversation/${conversation.id}`, {
			starterMessages: starter.chatMessages,
		});
	};
	return (
		<CopyMageAiChatLayout accountId={accountId}>
			<div className={css(s.starterRoot)}>
				<div className={css(s.starterHeader)}>
					<SparklesIcon className={css(s.sparklesIcon)} />
					<span className={css(s.starterAskLev)}>Ask Lev</span>
				</div>
				<Avatar2
					imgSrc={AIAssistantThinkingImageUrl}
					styleDeclaration={s.assistantThinkingAvatar}
					fallbackText='AI Assistant'
				/>
				<h1 className={css(s.helpMsg)}>What can I help you with today?</h1>
				{aiAssistantConversationStarterQuery.data ? (
					<div className={css(s.starterGrid)}>
						{aiAssistantConversationStarterQuery.data.map((starter, i) => {
							return (
								<button className={css(s.starterCard)} onClick={() => handleStarterPicked(starter)} key={i}>
									<span className={css(s.starterCardHeader)}>{starter.title}</span>
									<span className={css(s.starterCardSubtext)}>{starter.subTitle}</span>
								</button>
							);
						})}
					</div>
				) : null}
				<CopyMageChatPrompt
					value={prompt}
					onChange={setPrompt}
					styles={[s.starterInput]}
					onSubmit={() => handleStartConversation(prompt)}
					disabled={aiAssistantCreateConversationMutation.isPending}
				/>
			</div>
		</CopyMageAiChatLayout>
	);
}
